import { memo } from "react";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";

const ALLOWED_ELEMENTS = [
  "p",
  "br",
  "strong",
  "em",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "hr",
  "code",
  "h1",
  "h2",
  "h3",
  "h4",
];

function safeUrl(url: string): string | undefined {
  try {
    const parsed = new URL(url, "https://placeholder.invalid");
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return url.startsWith("//") ? `https:${url}` : url;
    }
  } catch {
    /* fall through */
  }
  return undefined;
}

const components: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  a: ({ href, children }) => {
    const safe = href ? safeUrl(href) : undefined;
    if (!safe) return <>{children}</>;
    return (
      <a href={safe} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

export const ProfileMarkdown = memo(function ProfileMarkdown({
  source,
  className,
}: {
  source: string | null | undefined;
  className?: string;
}) {
  if (!source || source.trim().length === 0) return null;
  return (
    <div
      className={cn(
        // Lean on @tailwindcss/typography for the bulk; tweak a few rules so the
        // output matches the existing card prose density.
        "prose prose-sm dark:prose-invert max-w-none",
        "prose-headings:font-semibold prose-headings:tracking-tight prose-headings:mb-1 prose-headings:mt-3 prose-headings:text-foreground",
        "prose-h1:text-lg prose-h1:leading-snug",
        "prose-h2:text-base prose-h2:leading-snug",
        "prose-h3:text-sm",
        "prose-h4:text-sm",
        "prose-p:my-2 prose-p:leading-relaxed prose-p:whitespace-pre-wrap",
        "prose-strong:text-foreground prose-strong:font-semibold",
        "prose-em:italic",
        "prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline prose-a:break-words",
        "prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-li:leading-relaxed",
        "prose-blockquote:border-l-2 prose-blockquote:border-indigo-200 dark:prose-blockquote:border-indigo-800 prose-blockquote:not-italic prose-blockquote:italic prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-300",
        "prose-code:before:hidden prose-code:after:hidden prose-code:font-normal prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[0.85em]",
        className,
      )}
    >
      <ReactMarkdown
        skipHtml
        allowedElements={ALLOWED_ELEMENTS}
        unwrapDisallowed
        components={components}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
});
